import LiveKitActionsBar from './LiveKitActionsBar';
import useActiveDevice from './useActiveDevice';

import beepAudio from '../../../assets/sounds/beep.mp3';
import TelemedicineContext from '../../../context/telemedicine';
import useDeviceList from '../../../hooks/useDeviceList';
import { createFile, removeFile } from '../../../services/telemedicine-file';
import { clearPreferredAudioOutputId, playAudioOutputTest, savePreferredAudioOutputId } from '../../../utils/livekit-audio-output';
import { normalizeUploadedFiles } from '../../../utils/uploaded-files';

import { showErrorToast, showSuccessToast } from '@amigoapp/doca-react';
import { useLocalParticipant } from '@livekit/components-react';
import { supportsAudioOutputSelection, Track } from 'livekit-client';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';

const LiveKitActionsBarContainer = () => {
	const { isCameraEnabled, isMicrophoneEnabled, localParticipant } = useLocalParticipant();
	const { telemedicine } = useContext(TelemedicineContext);

	const fileInputRef = useRef();
	const [isSubmittingFile, setIsSubmittingFile] = useState(false);
	const [callFiles, setCallFiles] = useState([]);
	const [showDeviceSettings, setShowDeviceSettings] = useState(false);
	const [showAttachPanel, setShowAttachPanel] = useState(false);

	const busyRef = useRef({});

	const showSpeakerSection = useMemo(() => supportsAudioOutputSelection(), []);

	const cameraDevices = useDeviceList('videoinput', showDeviceSettings);
	const micDevices = useDeviceList('audioinput', showDeviceSettings);
	const speakerDevices = useDeviceList('audiooutput', showDeviceSettings);

	const { activeDeviceId: activeCameraId, setActiveDevice: setActiveCamera } = useActiveDevice('videoinput');
	const { activeDeviceId: activeMicId, setActiveDevice: setActiveMic } = useActiveDevice('audioinput');
	const { activeDeviceId: activeSpeakerId, setActiveDevice: setActiveSpeaker } = useActiveDevice('audiooutput');

	const [switchingKind, setSwitchingKind] = useState(null);

	const switchDevice = useCallback(async (kind, run, errorText) => {
		setSwitchingKind(current => current || kind);

		try {
			await run();
		} catch (error) {
			showErrorToast(errorText);

			throw error;
		} finally {
			setSwitchingKind(null);
		}
	}, []);

	const [pendingSource, setPendingSource] = useState(null);

	const runExclusive = useCallback(async (source, operation, errorText) => {
		if (busyRef.current[source]) {
			return;
		}

		busyRef.current[source] = true;

		setPendingSource(source);

		try {
			await operation();
		} catch (error) {
			showErrorToast(errorText);
		} finally {
			busyRef.current[source] = false;

			setPendingSource(current => (current === source ? null : current));
		}
	}, []);

	const onAudioClick = useCallback(() => {
		runExclusive(
			Track.Source.Microphone,
			() => localParticipant.setMicrophoneEnabled(!isMicrophoneEnabled),
			`Não foi possível ${isMicrophoneEnabled ? 'desabilitar' : 'habilitar'} o áudio.`
		);
	}, [localParticipant, isMicrophoneEnabled, runExclusive]);

	const onVideoClick = useCallback(() => {
		runExclusive(
			Track.Source.Camera,
			() => localParticipant.setCameraEnabled(!isCameraEnabled),
			`Não foi possível ${isCameraEnabled ? 'desabilitar' : 'habilitar'} o vídeo.`
		);
	}, [localParticipant, isCameraEnabled, runExclusive]);

	const onToggleDeviceSettings = useCallback(() => {
		setShowDeviceSettings(current => !current);
		setShowAttachPanel(false);
	}, []);

	const onToggleAttachPanel = useCallback(() => {
		setShowAttachPanel(current => !current);
		setShowDeviceSettings(false);
	}, []);

	const onSelectCamera = useCallback(deviceId => {
		switchDevice('videoinput', () => setActiveCamera(deviceId), 'Não foi possível trocar a câmera.').catch(() => {});
	}, [switchDevice, setActiveCamera]);

	const onSelectMic = useCallback(deviceId => {
		switchDevice('audioinput', () => setActiveMic(deviceId), 'Não foi possível trocar o microfone.').catch(() => {});
	}, [switchDevice, setActiveMic]);

	const onSelectSpeaker = useCallback(deviceId => {
		switchDevice('audiooutput', () => setActiveSpeaker(deviceId), 'Não foi possível trocar a saída de áudio.')
			.then(() => savePreferredAudioOutputId(deviceId))
			.catch(() => clearPreferredAudioOutputId());
	}, [switchDevice, setActiveSpeaker]);

	const onTestSpeaker = useCallback(async () => {
		try {
			await playAudioOutputTest(beepAudio, activeSpeakerId);
		} catch (error) {
			showErrorToast('Não foi possível reproduzir na saída selecionada.');
		}
	}, [activeSpeakerId]);

	const onRemoveCallFile = useCallback(async id => {
		setCallFiles(current => current.filter(file => file.id !== id));

		try {
			await removeFile(id);
		} catch (error) { }
	}, []);

	const onSelectFiles = useCallback(async event => {
		const files = Array.from(event.target.files || []);

		setIsSubmittingFile(true);

		const { data } = await createFile(event.target.files);

		if (data) {
			showSuccessToast('Documento enviado com sucesso.');
			setCallFiles(current => [...current, ...normalizeUploadedFiles(data, files)]);
			window.socket.emit('telemedicine:file-sent', {
				room_id: telemedicine.id,
				user_id: telemedicine.user_id
			});
		} else {
			showErrorToast('Não foi possível criar o documento.');
		}

		setIsSubmittingFile(false);
		event.target.value = '';
	}, [telemedicine]);

	return (
		<LiveKitActionsBar
			activeCameraId={activeCameraId}
			activeMicId={activeMicId}
			activeSpeakerId={activeSpeakerId}
			callFiles={callFiles}
			cameraDevices={cameraDevices}
			fileInputRef={fileInputRef}
			isAudioEnabled={isMicrophoneEnabled}
			isPendingAudio={pendingSource === Track.Source.Microphone}
			isPendingVideo={pendingSource === Track.Source.Camera}
			isSubmittingFile={isSubmittingFile}
			isSwitchingCamera={switchingKind === 'videoinput'}
			isSwitchingMic={switchingKind === 'audioinput'}
			isSwitchingSpeaker={switchingKind === 'audiooutput'}
			isVideoEnabled={isCameraEnabled}
			micDevices={micDevices}
			showAttachPanel={showAttachPanel}
			showDeviceSettings={showDeviceSettings}
			showSpeakerSection={showSpeakerSection}
			speakerDevices={speakerDevices}
			onAudioClick={onAudioClick}
			onRemoveCallFile={onRemoveCallFile}
			onSelectCamera={onSelectCamera}
			onSelectFiles={onSelectFiles}
			onSelectMic={onSelectMic}
			onSelectSpeaker={onSelectSpeaker}
			onTestSpeaker={onTestSpeaker}
			onToggleAttachPanel={onToggleAttachPanel}
			onToggleDeviceSettings={onToggleDeviceSettings}
			onVideoClick={onVideoClick}
		/>
	);
};

export default LiveKitActionsBarContainer;
